import styled, { css } from "styled-components";

export interface NavTheme {
    white: string;
    green: string;
    gold: string;
    buttonBg: string;
    buttonBgHover: string;
    pTypography: string;
    headerTypography: string;
    dropdownVisible: string;
}

export const navTheme: NavTheme = {
    white: `#FFFFFF`,
    green: `#205C40`,
    gold: `#998D6B`,
    buttonBg: `#205C40`,
    buttonBgHover: `#017A6C`,
    pTypography: `
        color: #17313E;
        font-size: 1rem;
        line-height: 1.5;
        font-weight: 400;
        font-family: Lato;
    `,
    headerTypography: `
        display: block;
        color: #205C40;
        font-family: LibreBaskervilleBold;
        font-weight: 700;
    `,
    dropdownVisible: `
        visibility: visible;
        opacity: 1;
        transition: opacity .3s ease-in-out;
        pointer-events: auto;
    `,
};

// <============== General Navigation Styles ==============>

// ********** MasterNav ********** //
// Main component wrapper that is used to create space between the fixed nav and the page content.
export const MasterNav = styled.div`
    /* =============== Base Styles =============== */
    .sr-only:not(:focus):not(:active) {
        clip: rect(0 0 0 0);
        clip-path: inset(50%);
        height: 1px;
        overflow: hidden;
        position: absolute;
        white-space: nowrap;
        width: 1px;
    }

    a {
        color: ${navTheme.green};
        // Hover/Focus
        &:focus,
        &:hover {
            color: ${navTheme.green};
            text-decoration: underline;
        }
        // Sub Elements
        & > span[class^="TypographyStyle"] {
            // Targeting the spans created inside Link Elements
            color: ${navTheme.green};
        }
        &:focus > span[class^="TypographyStyle"],
        &:hover > span[class^="TypographyStyle"] {
            color: ${navTheme.green};
        }
    }

    h1,
    .h1,
    h2,
    .h2,
    h3,
    .h3,
    h4,
    .h4,
    h5,
    .h5,
    h6,
    .h6 {
        ${navTheme.headerTypography}
    }

    p {
        ${navTheme.pTypography}
    }

    img {
        max-width: 100%;
    }

    .button,
    .nav-btn {
        display: inline-block;
        margin-top: 1.5rem;
        width: 100%;
        max-width: 235px;
        padding: 0.75rem 1.5rem;
        background-color: ${navTheme.buttonBg};
        border-radius: 4px;
        color: ${navTheme.white};
        font-size: 1rem;
        font-family: "LatoMedium";
        line-height: 1;
        text-align: center;
        transition: background-color 0.3s ease-in-out;
        // Hover & Focus
        &:focus,
        &:hover {
            background-color: ${navTheme.buttonBgHover};
            transition: background-color 0.3s ease-in-out;
            text-decoration: none;
        }
        // Sub Elements
        & > span[class^="TypographyStyle"] {
            display: block;
            color: ${navTheme.white};
        }
        &:focus > span[class^="TypographyStyle"],
        &:hover > span[class^="TypographyStyle"] {
            color: ${navTheme.white};
        }
    }
    .iconButton {
        padding: 0;
        background: transparent;
        border: none;
        border-radius: 0;
        // Hover/Focus
        &:focus,
        &:hover {
            background: transparent;
        }
    }
`;

// ********** MasterNavContentWrapper ********** //
export const MasterNavContentWrapper = styled.div`
    position: fixed;
    width: 100%;
    background-color: ${navTheme.white};
    box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.16);
    z-index: 1289;
`;

// <============== Main Navigation Styles ==============>

// ********** MainNav ********** //
export const MainNav = styled.nav`
    display: flex;
    justify-content: space-between;
    margin: 0 auto;
    width: 100%;
    /* Small Devices, Tablets+ */
    @media screen and (min-width: 768px) {
        max-width: 768px;
    }
    /* Laptop+ */
    @media screen and (min-width: 992px) {
        max-width: 992px;
        justify-content: flex-start;
    }
    /* Desktop+ */
    @media screen and (min-width: 1200px) {
        max-width: 1200px;
    }

    // Mobile Nav Button
    .menu-trigger-button {
        // Media Queries
        /* Small Devices, Tablets */
        @media screen and (min-width: 992px) {
            display: none;
        }
    }
`;

// ********** TopNav ********** //
export const TopNav = styled.div`
    text-align: center;
    color: ${navTheme.white};
    font-size: 0.875rem;
    font-family: "LatoMedium";
    padding: 0.35rem 0 0.25rem;
    width: 100%;
    background-color: ${navTheme.green};
`;

// ********** Logo Block ********** //
export const LogoBlock = styled.div`
    width: 100%;
    max-width: 142px;
    padding: 0.5rem 0.25rem;
    display: flex;
    align-items: center;
    // Media Queries
    /* Large Mobile Plus */
    /* @media screen and (min-width: 417px) {
        margin-left: 64px;
    } */
    /* Medium Devices, Desktops */
    @media screen and (min-width: 992px) {
        max-width: 155px;
        margin-left: 1rem;
        padding: 0;
    }

    /* =============== Logo Link =============== */
    .logoBlock__link {
        line-height: 0;
    }
`;

// ********** Icon Block ********** //
export const IconBlock = styled.div`
    // Media Queries
    /* Medium Devices, Desktops */
    @media screen and (min-width: 992px) {
        margin-right: 0;
        margin-left: auto;
    }

    .iconBlock__list {
        height: 100%;
        display: flex;
    }

    .iconBlock__item {
        display: inline-block;
        height: 100%;
    }
    .icon_cart {
        display: flex;
        align-items: center;
        height: 100%;
    }

    .iconBlock__iconWrap {
        height: 100%;
        padding: 0 1rem;
        display: flex;
        align-items: center;
        line-height: 0;
        // Media Queries
        /* Small Devices, Tablets */
        @media screen and (min-width: 768px) {
            padding: 0 1.25rem;
        }
    }

    .iconBlock__icon {
        color: ${navTheme.green};
        // Hover/Focus
        &:focus,
        &:hover {
            color: ${navTheme.buttonBgHover};
        }
    }

    .iconBlock__link {
        // Hover/Focus - Change icon color
        &:focus .iconBlock__icon,
        &:hover .iconBlock__icon {
            color: ${navTheme.buttonBgHover};
        }
        // Hover/Focus - If there is a dropdown make it visible
        &:focus + .dropdown,
        &:hover + .dropdown,
        &[aria-expanded="true"] + .dropdown {
            ${navTheme.dropdownVisible}
        }
        // Sub Elements
        & > span[class^="TypographyStyle"] {
            position: relative;
        }
    }

    /* =============== Search =============== */
    .search__btn {
        // Hover/Focus - Change icon color
        &:focus .iconBlock__icon,
        &:hover .iconBlock__icon {
            color: ${navTheme.buttonBgHover};
        }
        // Open Search Dropdown
        &[aria-expanded="true"] + .dropdown {
            ${navTheme.dropdownVisible}
        }
    }

    /* ********** Search Content Styles ********** */

    .navSearch span[class^="IconWrapper"] {
        color: ${navTheme.green};
    }

    #headerSearch-MasterNavId {
        border: none;
        border-bottom: 2px ${navTheme.green} solid;
        border-radius: 0;
    }

    // Hard style to adjust for direct element styling from Popover component
    #headerSearch-MasterNavId-popover {
        position: relative !important;
        top: 0 !important;
        right: 0 !important;
        box-shadow: none;
    }

    /* =============== Account Management =============== */

    // Hide on mobile
    .iconBlock__item--account,
    .iconBlock__item--account-notSignedIn {
        // Media Queries
        /* Medium Devices, Desktops */
        @media screen and (max-width: 991px) {
            display: none;
        }
    }

    .iconBlock__item--account-notSignedIn {
        // Sub Elements
        .iconBlock__icon {
            margin-right: 5px;
        }

        .iconBlock__link span[class^="TypographyStyle"] {
            display: flex;
            align-items: center;
        }
    }

    .iconBlock__signInText {
        font-size: 0.875rem;
        line-height: 1;
    }

    /* =============== Cart =============== */
    .cart__itemCounter {
        padding: 2px 4px 1px 5px;
        height: 16px;
        width: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        top: -17px;
        right: 0px;
        background-color: ${navTheme.gold};
        border-radius: 100%;
        color: ${navTheme.white};
        font-size: 0.7rem;
        font-family: "LatoMedium";
        // Pseudos
        &:empty {
            visibility: hidden;
        }
    }
`;

// <==============  ==============>

// ********** Link Block ********** //
export const LinkBlock = styled.div`
    display: none;
    margin-left: 1.25rem;
    // Media Queries
    /* Medium Devices, Desktops */
    @media screen and (min-width: 992px) {
        display: block;
    }
`;

// ********** Nav LinkList ********** //
export const NavLinkList = styled.ul`
    display: flex;

    /* =============== Nav LinkList Item =============== */
    .navLinkList__item {
        display: inline-block;
        line-height: 1;
        // Hover/Focus parent
        &:focus > .navLinkList__link[aria-expanded="true"] + .dropdown,
        &:hover > .navLinkList__link[aria-expanded="true"] + .dropdown {
            ${navTheme.dropdownVisible}
        }
    }

    /* =============== Nav LinkList Link =============== */
    .navLinkList__link {
        padding: 1.875rem;
        position: relative;
        font-size: 1rem;
        font-weight: bold;
        // Pseudo classes
        &::after {
            content: "";
            opacity: 0;
            position: absolute;
            right: 0;
            bottom: 25%;
            left: 0;
            margin-right: auto;
            margin-left: auto;
            height: 2px;
            width: 70%;
            background-color: ${navTheme.gold};
            transition: opacity 0.3s linear;
        }
        // Hover/Focus
        &:focus,
        &:hover {
            color: ${navTheme.green};
            text-decoration: none;
        }
        // Hover/Focus Pseudos
        &:focus::after,
        &:hover::after,
        &[aria-expanded="true"]::after {
            opacity: 1;
            transition: opacity 0.3s linear;
        }
        // Hover/Focus - If there is a dropdown make it visible
        &[aria-expanded="true"] + .dropdown {
            ${navTheme.dropdownVisible}
        }
    }
`;

// ********** Nav Dropdown ********** //
// Add .dropdown--megaMenu modifier to the S.NavDropDown component to make any sub-menu a Mega Menu

// Props interface
interface NavDropDownProps {
    readonly menuWidth?: number;
    readonly parentWidth?: number;
}
// Component/CSS
export const NavDropDown = styled.ul<NavDropDownProps>`
    visibility: hidden;
    opacity: 0;
    position: absolute;
    background-color: ${navTheme.white};
    border-radius: 0 0 8px 8px;
    box-shadow: 0px 2px 2px 1px rgba(0, 0, 0, 0.1);
    transition: visibility 0.3s linear, opacity 0.2s ease-in-out;
    overflow: hidden;
    // Modifiers
    &.dropdown--megaMenu {
        width: 100%;
        max-width: 1000px;
        margin: 0 auto;
        right: 0;
        left: 0;
        pointer-events: none;
    }
    &.dropdown--menu {
        margin-left: ${props =>
            props.menuWidth && props.parentWidth
                ? `-${(props.menuWidth - props.parentWidth) / 2}px`
                : 0}; // Centers the menu under the parent by pulling it to the left based on current menu width and parent width
    }
    &.dropdown--search {
        max-width: 720px;
    }
`;

// ********** Dropdown Menu ********** //
export const DropdownMenu = styled.div`
    .dropdownMenu__list {
        margin-top: 10px;
        display: flex;
        flex-direction: column;
    }

    .dropdownMenu__link {
        padding: 1rem;
        width: 100%;
        font-size: 0.9375rem;
        font-family: "LatoMedium";
        line-height: 1;
        // If Button
        &[class^="ButtonWrapper"] {
            min-height: auto;
            background: none;
            color: ${navTheme.green};
            word-break: normal;
            border: none;
            border-radius: 0;
        }
        // Hover/Focus
        &:focus,
        &:hover,
        &[class^="ButtonWrapper"]:focus,
        &[class^="ButtonWrapper"]:hover,
        &:focus > span[class^="TypographyStyle"],
        &:hover > span[class^="TypographyStyle"],
        &:focus > span[class^="IconWrapper"],
        &:hover > span[class^="IconWrapper"] {
            color: ${navTheme.white};
            text-decoration: none;
        }
        &:hover,
        &:focus,
        &[class^="ButtonWrapper"]:focus,
        &[class^="ButtonWrapper"]:hover {
            background-color: ${navTheme.green};
            transition: none;
        }

        &:hover .dropdownMenu__link-icon,
        &:focus .dropdownMenu__link-icon {
            color: ${navTheme.white};
        }
        // Sub Elements
        & > span[class^="TypographyStyle"],
        &[class^="ButtonWrapper"] {
            display: flex;
            align-items: center;
        }
    }

    .dropdownMenu__link-icon {
        color: ${navTheme.green};
        margin-right: 10px;
    }

    .dropdownMenu__link-text {
        margin-top: 3px;
    }
`;

// ********** Mega Menu ********** //
export const MegaMenu = styled.div`
    padding: 1rem;

    // Media Queries
    /* Medium Devices, Desktops */
    @media screen and (min-width: 992px) {
        padding: 1.85rem 1.5rem;
        padding-left: 2.25rem;
    }

    .dropdown--search > & {
        padding-left: 1.5rem;
    }

    /* =============== Mega Menu Container =============== */
    .megaMenu__container {
        display: flex;
        margin-right: -1rem;
        margin-left: -1rem;
        // Modifiers
        &.megaMenu__container--d-column {
            // Changes flex (d)irection on the column
            flex-direction: column;
        }
    }

    /* =============== Mega Menu Column(s) =============== */
    .megaMenu__column {
        padding: 0 1rem;
        // Modifiers
        &.megaMenu__column--links,
        &.megaMenu__column--callout {
            flex: 0 0 33%;
            max-width: 33%;
        }
        &.megaMenu__column--CTA {
            flex: 0 0 66%;
            max-width: 66%;

            // Sub Elements
            & > .megaMenu__container > .megaMenu__column {
                flex: 0 0 50%;
                max-width: 50%;
            }
        }
        &.megaMenu__column--categories,
        &.megaMenu__column--search {
            flex: 0 0 100%;
            max-width: 100%;
        }
        &.megaMenu__column--fullButton {
            margin-top: 0.25rem;
            text-align: center;
        }
    }

    /* =============== Mega Menu Link List =============== */

    .megaMenu__linkList-header {
        position: relative;
        margin-bottom: 1rem;
        padding-bottom: 0.5rem;
        ${navTheme.headerTypography}
        font-size: 1rem;
        // Pseudo classes
        &::after {
            content: "";
            position: absolute;
            left: 0;
            bottom: 0;
            height: 1px;
            width: 100%;
            max-width: 160px;
            background-color: #dadcd7;
        }
    }

    .megaMenu__linkList-item {
        padding: 0.5rem 0;
    }

    /* =============== Category List =============== */
    .megaMenu__categoryList {
        padding-left: 0.75rem;
        column-count: 3;
    }

    .megaMenu__categoryItem {
        margin: 1rem 0;

        // Pseudo classes
        &:first-of-type {
            margin-top: 0;
        }
    }

    .megaMenu__categoryLink {
        font-size: 0.9375rem;
        // Sub Elements
        & > span[class^="TypographyStyle"] {
            display: flex;
            align-items: center;
        }
    }

    .megaMenu__categoryLink-icon {
        margin-right: 16px;
        max-height: 28px;
        max-width: 28px;
        color: ${navTheme.green};
    }

    .megaMenu__categoryLink-text {
        margin-top: 3px;
        font-family: "LatoMedium";
    }

    /* =============== CTA =============== */

    .megaMenu__CTA-header {
        margin-top: 0.65rem;
        margin-bottom: 1rem;
        ${navTheme.headerTypography}
        font-size: 1.125rem;
    }

    .megaMenu__CTA-text {
        font-size: 0.9rem;
    }

    /* =============== Callout =============== */

    .megaMenu__callout-link {
        // Hover/Focus
        &:focus .megaMenu__callout-img,
        &:hover .megaMenu__callout-img {
            transform: scale(1.3);
            transition: transform 0.4s ease-in-out;
        }
    }

    .megaMenu__callout-imgWrap {
        margin-bottom: 0.75rem;
        max-height: 230px;
        overflow: hidden;
    }

    .megaMenu__callout-img {
        transition: transform 0.4s ease-in-out;
    }

    .megaMenu__callout-header {
        margin-bottom: 0.75rem;
        ${navTheme.headerTypography}
        font-size: 1rem;
    }

    .megaMenu__callout-text {
        font-size: 0.875rem;
    }
`;
